import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDrag } from '@use-gesture/react';
import type { Application } from '@applicant-reviewer/api';
import { useUpdateApplicationStatus } from '../../../../hooks/mutations/useApplicationMutations';
import { useCreateInterview } from '../../../../hooks/mutations/useInterviewMutations';
import ZoomableScheduleCanvas from '../../../../components/availability/ZoomableScheduleCanvas';
import RejectCandidateModal from './RejectCandidateModal';
import HireOfferModal from './HireOfferModal';
import BenchCandidateModal from './BenchCandidateModal';
import type { WeeklyAvailability } from '@applicant-reviewer/api/availability';
import { DAYS_OF_WEEK, SLOT_STATUS, SLOTS_PER_DAY } from '@applicant-reviewer/api/availability';
import ApplicantQA from '../../../../components/applicant/ApplicantQA';
import { useManagerCandidateProfile } from '../../../../hooks/queries/useManagerCandidateProfile';
import { useManagerMedia, useManagerMediaDownload } from '../../../../hooks/queries/useManagerMedia';
import type { CandidateMedia } from '@applicant-reviewer/api';

function getApplicantName(app: Application): string {
  return [app.contactInfo?.firstName, app.contactInfo?.lastName].filter(Boolean).join(' ') || 'Unknown';
}

interface ReviewDeckProps {
  applicants: Application[];
  onBack?: () => void;
  initialApplicantId?: string;
}

const SPEED_OPTIONS = [1, 1.5, 2] as const;
const VIDEO_SOURCE = 'https://archive.org/download/BigBuckBunny_124/Content/big_buck_bunny_720p_surround.mp4';
const VIDEO_POSTER =
  'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2';

type SpeedRate = (typeof SPEED_OPTIONS)[number];

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return hash || 1;
}

function createSeededRandom(seed: number) {
  let internalSeed = seed || 1;
  return () => {
    internalSeed = (internalSeed + 0x6d2b79f5) | 0;
    let t = Math.imul(internalSeed ^ (internalSeed >>> 15), 1 | internalSeed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function getDistanceMiles(applicant: Application): string {
  const random = createSeededRandom(Math.abs(hashString(applicant.applicationId)));
  const miles = 1 + random() * 14;
  return miles.toFixed(1);
}

const generateMockAvailability = (seed: string): WeeklyAvailability => {
  const seeded = Math.abs(hashString(seed)) || 1;
  const random = createSeededRandom(seeded);
  return DAYS_OF_WEEK.map((dayOfWeek) => ({
    dayOfWeek,
    slots: Array.from({ length: SLOTS_PER_DAY }).map((_, index) => {
      const insideBusinessHours = index >= 12 && index <= 40;
      if (!insideBusinessHours) {
        return { index, status: SLOT_STATUS.UNAVAILABLE };
      }
      const roll = random();
      if (roll > 0.75) {
        return { index, status: SLOT_STATUS.PREFERRED };
      }
      if (roll > 0.45) {
        return { index, status: SLOT_STATUS.AVAILABLE };
      }
      return { index, status: SLOT_STATUS.UNAVAILABLE };
    }),
  }));
};

// ---------------------------------------------------------------------------
// MediaDownloadLink — fetches presigned URL and renders a download anchor
// ---------------------------------------------------------------------------

function mediaTypeLabel(type: CandidateMedia["type"]): string {
  switch (type) {
    case "video_answer": return "Video Answer";
    case "id_front": return "ID (Front)";
    case "id_back": return "ID (Back)";
    case "photo": return "Photo";
    default: return type;
  }
}

function MediaDownloadLink({ item }: { item: CandidateMedia }) {
  const { data, isLoading, isError } = useManagerMediaDownload(item.mediaId);

  if (isLoading) {
    return (
      <span className="inline-flex items-center text-xs text-gray-400">
        Loading…
      </span>
    );
  }

  if (isError || !data) {
    return (
      <span className="inline-flex items-center text-xs text-red-400">
        Unavailable
      </span>
    );
  }

  return (
    <a
      href={data.downloadUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 underline"
    >
      Download
    </a>
  );
}

// ---------------------------------------------------------------------------
// CandidateMediaSection — lists media items for the current application
// ---------------------------------------------------------------------------

function CandidateMediaSection({ applicationId }: { applicationId: string }) {
  const { data: mediaItems, isLoading, isError } = useManagerMedia(applicationId);

  if (isLoading) {
    return (
      <section className="px-5 pb-6">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Candidate Media
        </h3>
        <p className="text-sm text-gray-400">Loading media…</p>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="px-5 pb-6">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Candidate Media
        </h3>
        <p className="text-sm text-red-400">Failed to load media.</p>
      </section>
    );
  }

  if (!mediaItems || mediaItems.length === 0) {
    return null;
  }

  return (
    <section className="px-5 pb-6">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
        Candidate Media
      </h3>
      <div className="bg-gray-50 rounded-xl divide-y divide-gray-100">
        {mediaItems.map((item) => (
          <div key={item.mediaId} className="flex items-center justify-between px-4 py-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-gray-900">{mediaTypeLabel(item.type)}</span>
              <span className={`text-xs font-medium ${
                item.status === "ready" ? "text-green-600" :
                item.status === "failed" ? "text-red-500" :
                "text-yellow-600"
              }`}>
                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
              </span>
            </div>
            {item.status === "ready" && <MediaDownloadLink item={item} />}
          </div>
        ))}
      </div>
    </section>
  );
}

function ReviewDeck({ applicants, onBack, initialApplicantId }: ReviewDeckProps) {
  const sortedApplicants = applicants;
  const updateStatus = useUpdateApplicationStatus();
  const createInterview = useCreateInterview();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [speedIndex, setSpeedIndex] = useState(0);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isInterviewModalOpen, setIsInterviewModalOpen] = useState(false);
  const [isHireModalOpen, setIsHireModalOpen] = useState(false);
  const [isBenchModalOpen, setIsBenchModalOpen] = useState(false);
  const [interviewMode, setInterviewMode] = useState<'in_person' | 'virtual'>('in_person');
  const [interviewScheduling, setInterviewScheduling] = useState<'auto' | 'manual'>('auto');
  const [isAiExplainerOpen, setIsAiExplainerOpen] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  useEffect(() => {
    if (!initialApplicantId) {
      setCurrentIndex(0);
      return;
    }
    const idx = sortedApplicants.findIndex((applicant) => applicant.applicationId === initialApplicantId);
    setCurrentIndex(idx >= 0 ? idx : 0);
  }, [initialApplicantId, sortedApplicants]);

  useEffect(() => {
    if (currentIndex >= sortedApplicants.length) {
      setCurrentIndex(sortedApplicants.length > 0 ? sortedApplicants.length - 1 : 0);
    }
  }, [sortedApplicants.length, currentIndex]);

  const currentApplicant = sortedApplicants[currentIndex];
  const selectedSpeed: SpeedRate = SPEED_OPTIONS[speedIndex];

  const { data: candidateProfile } = useManagerCandidateProfile(
    currentApplicant?.candidateId ?? ''
  );

  const distanceLabel = useMemo(() => {
    if (!currentApplicant) return '';
    return `${getDistanceMiles(currentApplicant)} miles away`;
  }, [currentApplicant]);

  const scheduleAvailability = useMemo(
    () => generateMockAvailability(currentApplicant?.applicationId ?? 'default'),
    [currentApplicant?.applicationId]
  );

  const resetDecisionState = useCallback(() => {
    setIsRejectModalOpen(false);
    setIsInterviewModalOpen(false);
    setInterviewMode('in_person');
    setInterviewScheduling('auto');
  }, []);

  const advanceToNext = useCallback(() => {
    resetDecisionState();
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    setCurrentIndex((prev) => prev + 1);
  }, [resetDecisionState]);

  const closeRejectModal = useCallback(() => {
    setIsRejectModalOpen(false);
  }, []);

  const closeInterviewModal = useCallback(() => {
    setIsInterviewModalOpen(false);
    setInterviewMode('in_person');
    setInterviewScheduling('auto');
  }, []);

  const openRejectModal = useCallback(() => {
    if (!currentApplicant) return;
    setIsRejectModalOpen(true);
  }, [currentApplicant]);

  const openInterviewModal = useCallback(() => {
    if (!currentApplicant) return;
    setInterviewMode('in_person');
    setInterviewScheduling('auto');
    setIsInterviewModalOpen(true);
  }, [currentApplicant]);

  const confirmReject = useCallback(
    (reason: string, notes?: string) => {
      if (!currentApplicant) return;
      updateStatus.mutate({
        applicationId: currentApplicant.applicationId,
        payload: { status: 'rejected', reason: notes ?? reason },
      });
      advanceToNext();
    },
    [currentApplicant, advanceToNext, updateStatus]
  );

  const confirmInterview = useCallback(() => {
    if (!currentApplicant) return;
    // First move the application status to 'interview'
    updateStatus.mutate(
      {
        applicationId: currentApplicant.applicationId,
        payload: { status: 'interview' },
      },
      {
        onSuccess: () => {
          // Then create the interview record with the scheduling details
          const interviewType = interviewMode === 'in_person' ? 'in_person' : 'virtual';
          // Generate proposed slots: next 3 business days at 10am if auto, or leave empty for manual
          const proposedSlots: string[] = interviewScheduling === 'auto'
            ? Array.from({ length: 3 }, (_, i) => {
                const d = new Date();
                d.setDate(d.getDate() + i + 1);
                d.setHours(10, 0, 0, 0);
                return d.toISOString();
              })
            : [];
          createInterview.mutate({
            applicationId: currentApplicant.applicationId,
            candidateId: currentApplicant.candidateId,
            jobPostingId: currentApplicant.jobPostingId,
            candidateName: getApplicantName(currentApplicant),
            role: '',
            type: interviewType,
            duration: 60,
            availableSlots: proposedSlots,
          });
        },
      }
    );
    advanceToNext();
  }, [currentApplicant, advanceToNext, updateStatus, createInterview, interviewMode, interviewScheduling]);

  const confirmBench = useCallback(() => {
    if (!currentApplicant) return;
    updateStatus.mutate({
      applicationId: currentApplicant.applicationId,
      payload: { status: 'benched' },
    });
    setIsBenchModalOpen(false);
    advanceToNext();
  }, [currentApplicant, advanceToNext, updateStatus]);

  const handleBench = useCallback(() => {
    if (!currentApplicant) return;
    setIsBenchModalOpen(true);
  }, [currentApplicant]);

  const handleSpeedToggle = useCallback(() => {
    setSpeedIndex((prev) => {
      const next = (prev + 1) % SPEED_OPTIONS.length;
      if (videoRef.current) {
        videoRef.current.playbackRate = SPEED_OPTIONS[next];
      }
      return next;
    });
  }, []);

  const handlePrevApplicant = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const handleNextApplicant = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex < sortedApplicants.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, sortedApplicants.length]);

  useEffect(() => {
    setVideoError(false);

    if (videoRef.current) {
      videoRef.current.playbackRate = selectedSpeed;
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {});
      }
    }
  }, [selectedSpeed, currentApplicant]);

  const bindDeck = useDrag(
    ({ last, movement: [mx, my] }) => {
      if (!currentApplicant || isRejectModalOpen || isInterviewModalOpen) return;
      if (!last) return;
      if (Math.abs(mx) < Math.abs(my) || Math.abs(mx) < 100) {
        return;
      }
      if (mx > 0) {
        openInterviewModal();
      } else {
        openRejectModal();
      }
    },
    {
      filterTaps: true,
      axis: undefined,
      threshold: 15,
    }
  );

  if (!currentApplicant) {
    return (
      <div
        {...bindDeck()}
        className="flex-1 flex flex-col items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 text-center px-8"
        style={{ touchAction: 'none' }}
      >
        <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
          <svg className="w-12 h-12 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">All Caught Up!</h2>
        <p className="text-gray-600">
          You have reviewed {sortedApplicants.length} applicants. More will appear here soon.
        </p>
      </div>
    );
  }

  return (
    <div
      {...bindDeck()}
      className="h-full flex flex-col bg-white"
      style={{ touchAction: 'none' }}
    >
      <div className="flex-1 overflow-y-auto" ref={scrollContainerRef} style={{ touchAction: 'pan-y' }}>
        <div className="relative">
          {videoError ? (
            <div className="w-full h-[320px] sm:h-[360px] bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
              <div className="text-center px-6">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-white text-sm font-medium">Video Interview Recording</p>
                <p className="text-gray-400 text-xs mt-1">Preview not available</p>
              </div>
            </div>
          ) : (
            <video
              ref={videoRef}
              className="w-full h-[320px] sm:h-[360px] object-cover"
              poster={VIDEO_POSTER}
              controls
              playsInline
              autoPlay={autoPlay}
              onError={() => setVideoError(true)}
            >
              <source src={VIDEO_SOURCE} type="video/mp4" />
            </video>
          )}

          <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

          <div className="absolute top-4 right-4 z-20 flex flex-col items-end gap-2">
            <button
              type="button"
              onClick={handleSpeedToggle}
              className="flex items-center rounded-full bg-black/70 px-4 py-2 text-xs font-semibold text-white backdrop-blur"
            >
              {selectedSpeed}x
            </button>

            {/* Autoplay Toggle */}
            <button
              type="button"
              onClick={() => setAutoPlay(!autoPlay)}
              className="flex items-center gap-1.5 rounded-full bg-black/70 px-2.5 py-1.5 backdrop-blur"
              title={autoPlay ? "Autoplay On" : "Autoplay Off"}
            >
              <span className="text-[10px] font-medium text-white">Auto</span>
              <div className={`w-6 h-3 rounded-full transition-colors ${autoPlay ? 'bg-green-500' : 'bg-gray-400'}`}>
                <div className={`w-2.5 h-2.5 rounded-full bg-white transition-transform ${autoPlay ? 'translate-x-3' : 'translate-x-0.5'} mt-0.5`} />
              </div>
            </button>
          </div>

          <div className="absolute bottom-0 left-0 right-0 px-5 pb-5 z-10">
            <h1 className="text-2xl font-bold text-white">{getApplicantName(currentApplicant)}</h1>
            <p className="text-sm text-gray-200 mt-1">{distanceLabel}</p>
          </div>
        </div>

        <div className="grid grid-cols-[auto_auto_1fr_1fr_auto] gap-4 border-b border-gray-100 items-center pr-5">
          {/* Back Button */}
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="ml-5 h-[40px] w-[80px] flex items-center justify-center rounded-full bg-[#8E8E8F] text-[18px] font-bold text-white hover:bg-gray-600 transition-colors"
            >
              Back
            </button>
          )}

          {/* Status Pill */}
          <div className="ml-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
              currentApplicant.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
              currentApplicant.status === 'interview' ? 'bg-green-100 text-green-700' :
              currentApplicant.status === 'benched' ? 'bg-yellow-100 text-yellow-700' :
              currentApplicant.status === 'offered' ? 'bg-purple-100 text-purple-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {currentApplicant.status}
            </span>
          </div>

          <button
            type="button"
            onClick={() => setIsScheduleModalOpen(true)}
            className="text-left px-5 py-3 cursor-pointer"
          >
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Schedule Fit
            </p>
            <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-orange-600 to-orange-500"
                style={{ width: `${currentApplicant.scheduleFit ?? 0}%` }}
              />
            </div>
          </button>
          <button
            type="button"
            onClick={() => setIsAiExplainerOpen(true)}
            className="text-left px-5 py-3 cursor-pointer"
          >
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">AI Score</p>
            <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-purple-600 to-purple-500"
                style={{ width: `${currentApplicant.aiScore ?? 0}%` }}
              />
            </div>
          </button>
          <div className="flex items-center gap-2 text-sm font-bold text-gray-500 whitespace-nowrap">
            <button
              type="button"
              onClick={handlePrevApplicant}
              disabled={currentIndex === 0}
              className={`p-1 rounded-full hover:bg-gray-100 ${currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            <span>{currentIndex + 1} / {sortedApplicants.length}</span>

            <button
              type="button"
              onClick={handleNextApplicant}
              disabled={currentIndex === sortedApplicants.length - 1}
              className={`p-1 rounded-full hover:bg-gray-100 ${currentIndex === sortedApplicants.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>

        <section className="px-5 py-6">
          <ApplicantQA questions={currentApplicant.answers ?? []} layout="compact" />
        </section>

        {candidateProfile && (
          <section className="px-5 pb-6">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Candidate Profile
            </h3>
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
              {(candidateProfile.firstName || candidateProfile.lastName) && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Name</span>
                  <span className="font-medium text-gray-900">
                    {[candidateProfile.firstName, candidateProfile.lastName].filter(Boolean).join(' ')}
                  </span>
                </div>
              )}
              {candidateProfile.phone && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Phone</span>
                  <span className="font-medium text-gray-900">{candidateProfile.phone}</span>
                </div>
              )}
              {candidateProfile.email && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium text-gray-900">{candidateProfile.email}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Profile Complete</span>
                <span className={`font-medium ${candidateProfile.profileComplete ? 'text-green-600' : 'text-yellow-600'}`}>
                  {candidateProfile.profileComplete ? 'Yes' : 'Incomplete'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">ID Verification</span>
                <span className={`font-medium ${candidateProfile.idFrontMediaId && candidateProfile.idBackMediaId ? 'text-green-600' : 'text-gray-400'}`}>
                  {candidateProfile.idFrontMediaId && candidateProfile.idBackMediaId ? 'Captured' : 'Not captured'}
                </span>
              </div>
            </div>
          </section>
        )}

        <CandidateMediaSection applicationId={currentApplicant.applicationId} />
      </div>

      <div className="bg-white border-t border-gray-200 px-5 py-4">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={openRejectModal}
            disabled={updateStatus.isPending}
            className="flex-1 h-[40px] rounded-full bg-[#dd1b0a] hover:bg-[#b01608] text-white text-[18px] font-bold shadow-sm transition-colors disabled:opacity-50"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={handleBench}
            disabled={updateStatus.isPending}
            className="flex-1 h-[40px] rounded-full bg-[#FFC107] hover:bg-[#FFB300] text-white text-[18px] font-bold shadow-sm transition-colors disabled:opacity-50"
          >
            Bench
          </button>
          <button
            type="button"
            onClick={openInterviewModal}
            disabled={updateStatus.isPending}
            className="flex-1 h-[40px] rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[18px] font-bold shadow-lg transition-colors disabled:opacity-50"
          >
            Interview
          </button>
        </div>
      </div>

      <RejectCandidateModal
        isOpen={isRejectModalOpen}
        onClose={closeRejectModal}
        onConfirm={confirmReject}
        candidateName={currentApplicant ? getApplicantName(currentApplicant) : undefined}
      />

      <HireOfferModal
        isOpen={isHireModalOpen}
        onClose={() => setIsHireModalOpen(false)}
        candidateName={currentApplicant ? getApplicantName(currentApplicant) : ''}
        applicationId={currentApplicant?.applicationId ?? ''}
      />

      <BenchCandidateModal
        isOpen={isBenchModalOpen}
        onClose={() => setIsBenchModalOpen(false)}
        onConfirm={confirmBench}
        candidateName={currentApplicant ? getApplicantName(currentApplicant) : ''}
      />

      {isInterviewModalOpen && (
        <div className="fixed inset-0 z-40 flex items-end">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            onClick={closeInterviewModal}
            aria-label="Dismiss interview modal"
          />
          <div className="relative w-full rounded-t-3xl bg-white px-6 pb-6 pt-4 space-y-5 shadow-2xl">
            <div className="mx-auto h-1.5 w-12 rounded-full bg-gray-200" />
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900">Schedule {currentApplicant ? getApplicantName(currentApplicant) : ''}</p>
              <p className="text-sm text-gray-500 mt-1">Choose how you want to meet and schedule.</p>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Format
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'In Person', value: 'in_person' as const },
                    { label: 'Virtual', value: 'virtual' as const },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setInterviewMode(option.value)}
                      className={`
                        h-12 rounded-xl border text-sm font-semibold transition
                        ${
                          interviewMode === option.value
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-gray-200 text-gray-700 hover:border-gray-400'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Scheduling
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Auto Schedule', value: 'auto' as const },
                    { label: 'Set Time', value: 'manual' as const },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setInterviewScheduling(option.value)}
                      className={`
                        h-12 rounded-xl border text-sm font-semibold transition
                        ${
                          interviewScheduling === option.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-700 hover:border-gray-400'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={closeInterviewModal}
                className="flex-1 h-12 rounded-xl border border-gray-300 text-gray-600 font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmInterview}
                className="flex-1 h-12 rounded-xl font-semibold text-white bg-emerald-600 hover:bg-emerald-700"
              >
                Send Invite
              </button>
            </div>
          </div>
        </div>
      )}

      {isScheduleModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            onClick={() => setIsScheduleModalOpen(false)}
            aria-label="Dismiss schedule modal"
          />
          <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Schedule</p>
                <p className="text-lg font-semibold text-gray-900">{currentApplicant ? getApplicantName(currentApplicant) : ''}</p>
              </div>
              <button
                type="button"
                onClick={() => setIsScheduleModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close schedule modal"
              >
                ✕
              </button>
            </div>
            <div className="h-[420px] overflow-auto">
              <ZoomableScheduleCanvas
                availability={scheduleAvailability}
                onChange={() => {}}
                readOnly
                isManagerMode
              />
            </div>
          </div>
        </div>
      )}

      {isAiExplainerOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            onClick={() => setIsAiExplainerOpen(false)}
            aria-label="Dismiss AI explainer"
          />
          <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">AI Reasoning</p>
                <h3 className="text-lg font-semibold text-gray-900">Model Explanation</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAiExplainerOpen(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close AI explainer"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Explanation of AI reasoning goes here. This feature will automatically become active once the program has
              seen enough candidates to determine what successful candidates have in common.
            </p>
            <button
              type="button"
              onClick={() => setIsAiExplainerOpen(false)}
              className="w-full h-11 rounded-xl bg-indigo-600 text-white font-semibold"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReviewDeck;
